import { useIntl } from 'react-intl';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import Image from '@alltrails/core/components/Image';
import getStaticMapUrl from '@alltrails/core/utils/getStaticMapUrl';
import IconButton from '@alltrails/denali/components/IconButton';
import Expand from '@alltrails/denali/icons/Expand';
import HeroImage from '../HeroImage/HeroImage';
import styles from './StaticMapButton.module.scss';

type StaticMapButtonProps = {
  className: string;
  imageAlt: string;
  mapId: number;
  onClick: (isExpandButtonClicked: boolean) => void;
  variant: 'inset' | 'tall';
};

const StaticMapButton = ({ className, imageAlt, mapId, onClick, variant }: StaticMapButtonProps) => {
  const intl = useIntl();
  const publicBaseUrl = usePublicBaseUrl();
  const src = getStaticMapUrl({ baseUrl: publicBaseUrl, id: mapId, scale: 2, size: variant === 'tall' ? '368x372' : '368x182', type: 'maps' });
  // Names the button and labels the icon's tooltip so speech input users can say what they see.
  const expandLabel = intl.formatMessage({ defaultMessage: 'Expand map' });

  return (
    <div className={className}>
      <button aria-label={expandLabel} className={styles.button} onClick={() => onClick(false)}>
        {variant === 'tall' ? (
          <HeroImage alt={imageAlt} photoUrl={src} smallGridImage />
        ) : (
          <Image
            alt={imageAlt}
            fill
            loading="eager"
            /**
             * Responsive image sizing calculation for 2-column width in 6-column grid
             *
             * Breakpoint: 1216px = $breakpoint-md-size + 2 * $breakpoint-md-margin
             *
             * Below 1216px (dynamic viewport-based):
             *   Full: 2 * (((100vw - (2 * var(--breakpoint-md-margin))) - (5 * var(--space-100))) / 6) + (3 * var(--space-100))
             *   Simplified: calc((200vw - 17rem) / 6 + 1.5rem)
             *
             * Above 1216px (fixed container width):
             *   Full: 2 * ((var(--breakpoint-md-size) - (5 * var(--space-100))) / 6) + (3 * var(--space-100))
             *   Simplified: 372px
             *
             * Note: Using separate conditions instead of min/max due to Safari compatibility issues
             */
            sizes="(max-width: 1216px) calc((200vw - 17rem) / 6 + 1.5rem), 372px"
            src={src}
          />
        )}
      </button>
      {/* Decorative: the map button it sits on opens the same map, so it is hidden from assistive tech and the tab order. */}
      <div aria-hidden className={styles.expand}>
        <IconButton elevated icon={{ Component: Expand }} onClick={() => onClick(true)} tabIndex={-1} title={expandLabel} variant="ghost" />
      </div>
    </div>
  );
};

export default StaticMapButton;
